import React, { useState } from 'react';
import { Header } from './components/Header';
import { Navigation } from './components/Navigation';
import { StatusTab } from './components/StatusTab';
import { CompetitionsTab } from './components/CompetitionsTab';
import { ProfileTab } from './components/ProfileTab';
import { LoginScreen } from './components/LoginScreen';
import { CreateCompetitionModal } from './components/CreateCompetitionModal';
import { JoinCompetitionModal } from './components/JoinCompetitionModal';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useAuth } from './hooks/useAuth';
import { useCompetitions, useUserActivities, useLeaderboard, useUserProfile } from './hooks/useFirestore';
import { useSync } from './hooks/useSync';
import { calculateFilteredKm } from './utils/calculations';
import { TIME_FILTERS, TABS } from './constants/config';

// Import admin tools for development
import './utils/adminTools';

function App() {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState(TABS.STATUS);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [timeFilter, setTimeFilter] = useState(TIME_FILTERS.WEEK);
  const [isHandlingCallback, setIsHandlingCallback] = useState(false);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);

  // Firestore data
  const competitions = useCompetitions(user?.uid);
  const activities = useUserActivities(user?.uid);
  const leaderboard = useLeaderboard();
  const [userProfile] = useUserProfile(user?.uid, user?.displayName);
  
  // Sync
  const { syncActivities, syncing } = useSync(
    user, 
    userProfile, 
    leaderboard.find(l => l.id === user?.uid)?.totalKm || 0,
    activities || []
  );

  // Calculate filtered km
  const filteredKm = calculateFilteredKm(activities || [], timeFilter);

  // Handle tab change with smart competition closing
  const handleTabChange = (newTab) => {
    // If clicking Competitions tab while already on it, close the open competition
    if (newTab === TABS.COMPETITIONS && activeTab === TABS.COMPETITIONS && selectedCompetitionId) {
      setSelectedCompetitionId(null);
      return;
    }
    setActiveTab(newTab);
  };

  // Handle Google redirect result (for mobile login)
  React.useEffect(() => {
    const handleGoogleRedirect = async () => {
      try {
        const { handleRedirectResult } = await import('./services/auth');
        const redirectUser = await handleRedirectResult();
        if (redirectUser) {
          console.log('Google login successful via redirect:', redirectUser.email);
        }
      } catch (error) {
        console.error('Error handling Google redirect:', error);
        if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/no-redirect-result') {
          alert('Kunne ikke logge inn: ' + error.message);
        }
      }
    };

    // Kjør umiddelbart når appen starter
    handleGoogleRedirect();
  }, []);

  // Handle Strava OAuth callback
  React.useEffect(() => {
    const handleStravaCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');

      if (error) {
        alert('Tilgang til Strava ble avvist');
        window.history.replaceState({}, '', '/');
        return;
      }

      if (code && user?.uid && !isHandlingCallback) {
        setIsHandlingCallback(true);
        try {
          const { exchangeToken } = await import('./services/strava');
          const { updateUserProfile } = await import('./services/firestore');
          
          const tokenData = await exchangeToken(code);
          
          // Lagre tokens (merge: true bevarer eksisterende navn/avatar)
          await updateUserProfile(user.uid, {
            stravaAccessToken: tokenData.access_token,
            stravaRefreshToken: tokenData.refresh_token,
            stravaExpiresAt: tokenData.expires_at,
            stravaAthleteId: tokenData.athlete.id,
            stravaConnected: true,
            stravaConnectedAt: new Date().toISOString()
          });

          alert('Strava koblet til! Du kan nå synkronisere aktiviteter.');
          window.history.replaceState({}, '', '/');
          setActiveTab(TABS.PROFILE);
        } catch (err) {
          console.error('Error handling callback:', err);
          alert('Kunne ikke koble til Strava: ' + err.message);
          window.history.replaceState({}, '', '/');
        } finally {
          setIsHandlingCallback(false);
        }
      }
    };

    if (user && !loading) {
      handleStravaCallback();
    }
  }, [user, loading, isHandlingCallback]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header user={user} />
      
      <main className="container mx-auto px-4 py-6">
        {activeTab === TABS.STATUS && (
          <StatusTab 
            filteredKm={filteredKm}
            timeFilter={timeFilter}
            onTimeFilterChange={setTimeFilter}
            syncing={syncing}
            onSync={syncActivities}
            isAnonymous={user?.isAnonymous}
            competitions={competitions || []}
            userId={user?.uid}
            leaderboard={leaderboard || []}
            onCompetitionClick={(id) => {
              setSelectedCompetitionId(id);
              setActiveTab(TABS.COMPETITIONS);
            }}
            onShowJoinModal={() => setShowJoinModal(true)}
            onShowCreateModal={() => setShowCreateModal(true)}
          />
        )}
        {activeTab === TABS.COMPETITIONS && (
          <CompetitionsTab 
            competitions={competitions || []}
            userId={user?.uid}
            userProfile={userProfile}
            leaderboard={leaderboard || []}
            initialSelectedCompId={selectedCompetitionId}
            onCompetitionChange={(id) => setSelectedCompetitionId(id)}
            onSync={syncActivities}
            syncing={syncing}
          />
        )}
        {activeTab === TABS.PROFILE && (
          <ProfileTab 
            user={user}
            userProfile={userProfile}
            activities={activities || []}
            leaderboard={leaderboard || []}
          />
        )}
      </main>

      <Navigation activeTab={activeTab} onTabChange={handleTabChange} />

      {showCreateModal && (
        <CreateCompetitionModal 
          userId={user?.uid}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showJoinModal && (
        <JoinCompetitionModal 
          userId={user?.uid}
          onClose={() => setShowJoinModal(false)}
          onSuccess={async (competitionId) => {
            // Recalculate competition km for the user
            const { getDocs, query, where } = await import('firebase/firestore');
            const { getCompetitionsCollection, updateLeaderboard } = await import('./services/firestore');
            const { calculateKmForPeriod } = await import('./utils/calculations');
            
            const competitionsQuery = query(
              getCompetitionsCollection(),
              where('participants', 'array-contains', user.uid)
            );
            const competitionsSnapshot = await getDocs(competitionsQuery);
            const userCompetitions = competitionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const competitionStats = {};
            for (const comp of userCompetitions) {
              const startDate = comp.startDate || comp.createdAt;
              if (startDate) {
                const endDate = comp.endDate || new Date().toISOString();
                const kmInPeriod = calculateKmForPeriod(activities || [], startDate, endDate);
                competitionStats[comp.id] = kmInPeriod;
              }
            }

            await updateLeaderboard(user.uid, {
              name: String(userProfile.name || user.displayName || "Løper"),
              avatar: String(userProfile.avatar || "🏃‍♂️"),
              totalKm: leaderboard.find(l => l.id === user.uid)?.totalKm || 0,
              competitionKm: competitionStats,
              lastSync: new Date().toISOString()
            });
            
            // Bytt til konkurranser-fanen og velg den nye konkurransen
            setSelectedCompetitionId(competitionId);
            setActiveTab(TABS.COMPETITIONS);
          }}
        />
      )}
    </div>
  );
}

export default App;
